<template>
	<div ref="slider"
		 class="slideunlock"
		 :class="{
			'is-disabled': disabled,
			'is-noanimate': noanimate,
			'is-circle': circle,
			'is-complete': IsComplete,
			'is-themed': !!color
		}"
		 :style="slideUnlockStyle"
		 @mousemove="slideMoving"
		 @mouseup="slideFinish"
		 @touchmove="slideMoving"
		 @touchend="slideFinish">
		<div class="slideunlock-progressbar" :style="progressBarStyle" />
		<div class="slideunlock-text" :style="Text" aria-hidden="true">
			{{ message }}
		</div>

		<div ref="handler"
			 class="slideunlock-handler"
			 role="slider"
			 :tabindex="disabled || IsComplete ? -1 : 0"
			 :aria-label="text"
			 :aria-valuetext="IsComplete ? successText : null"
			 aria-valuemin="0"
			 aria-valuemax="100"
			 :aria-valuenow="percentComplete"
			 :aria-disabled="disabled ? 'true' : null"
			 :style="handlerStyle"
			 @mousedown="slideStart"
			 @touchstart="slideStart"
			 @keydown="keyDown" />
	</div>
</template>

<script>

	// This component is based on https://raw.githubusercontent.com/joseph2/vue-slide-unlock/
	// Modifications:
	// * Ported to work in vue 2
	// * "easy-ease" dependency removed
	// * sass dependency removed
	// * improved document event management
	// * fixed inconsistent dragging behavior when the pointer is left or right of the slider.
	// * excuse me, 0 is a perfectly valid x-coordinate.
	// * added "requireReleaseToComplete" property
	// * added "color" property: colors the handle and the border only
	// * keyboard support: the handle is a focusable slider; the arrow keys move it a quarter at a time, and reaching the end completes
	// * I'm starting to think I should have just written my own slide button component.

	/*
	MIT License

	Copyright(c) 2021 Anton Zolotov

	Permission is hereby granted, free of charge, to any person obtaining a copy
	of this software and associated documentation files(the "Software"), to deal
		in the Software without restriction, including without limitation the rights
	to use, copy, modify, merge, publish, distribute, sublicense, and / or sell
	copies of the Software, and to permit persons to whom the Software is
	furnished to do so, subject to the following conditions:

	The above copyright notice and this permission notice shall be included in all
	copies or substantial portions of the Software.

	THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
	IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
		FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.IN NO EVENT SHALL THE
	AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
	LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
		OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
	SOFTWARE.
	*/

	function throttle(fn, delay)
	{
		var interval;
		var queued;
		return function ()
		{
			if (!interval)
			{
				fn.apply(this, arguments);
				interval = setInterval(function ()
				{
					if (queued)
					{
						fn.apply(this, queued.args);
						queued = null;
					}
					else
					{
						clearInterval(interval);
						interval = null;
					}
				}, delay);
			}
			else
				queued = { args: arguments };
		};
	}

	export default {
		name: "SlideUnlock",
		props: {
			autoWidth: {
				type: Boolean,
				default: true
			},
			width: {
				type: Number,
				default: 400
			},
			height: {
				type: Number,
				default: 60
			},
			text: {
				type: String,
				default: "slide to unlock"
			},
			successText: {
				type: String,
				default: "success"
			},
			circle: {
				type: Boolean,
				default: true
			},
			disabled: {
				type: Boolean,
				default: false
			},
			noanimate: {
				type: Boolean,
				default: false
			},
			requireReleaseToComplete: {
				type: Boolean,
				default: false
			},
			/** Optional CSS color of the handle and the border (e.g. to match the kind of action).  The track stays neutral. */
			color: {
				type: String,
				default: ""
			}
		},
		data()
		{
			return {
				CanMove: false,
				IsComplete: false,
				StartPositionWindow: 0,
				HandlerPosition: 0,
				Handler: {
					left: 0
				},
				Progress: {
					width: 0
				},
				Text: {
					opacity: 1
				},
				fadeText: () => { }
			}
		},
		created()
		{
			this.fadeText = throttle(this.fadeTextNow, 33);
		},
		computed:
		{
			slideUnlockStyle()
			{
				let style = {
					width: this.autoWidth ? "auto" : this.width + "px",
					height: this.height + "px"
				};
				if (this.color)
					style["--su-color-theme"] = this.color;
				return style;
			},
			percentComplete()
			{
				if (this.IsComplete)
					return 100;
				// HandlerPosition is not reset when a drag snaps back, so only a handle that is away from the start counts.
				if (!this.Handler.left || this.Handler.left === "0px")
					return 0;
				// sliderWidth (not the width property) so that auto-width sliders report the right value too.
				let travel = this.sliderWidth - this.height;
				return travel > 0 ? Math.max(0, Math.min(99, Math.round(this.HandlerPosition * 100 / travel))) : 0;
			},
			progressBarStyle()
			{
				return {
					width: this.Progress.width,
					height: this.height + "px",
					background: this.Progress.background,
					borderRadius: this.circle
						? !this.IsComplete
							? (this.height / 2 + "px 0 0 " + this.height / 2 + "px")
							: (this.height / 2 + "px ")
						: 0
				}
			},
			handlerStyle()
			{
				return {
					left: this.Handler.left,
					width: this.height + "px",
					height: this.height + "px"
				}
			},
			message()
			{
				return this.IsComplete ? this.successText : this.text
			},
			sliderWidth()
			{
				return this.autoWidth
					? this.$refs.slider.clientWidth - (parseInt(getComputedStyle(document.documentElement).getPropertyValue("--su-size-padding").replace("px", ""), 10) * 2)
					: this.width
			}
		},
		watch:
		{
			autoWidth()
			{
				if (this.IsComplete)
				{
					const THIS = this
					setTimeout(() =>
					{
						THIS.Progress.width = THIS.sliderWidth + "px"
					}, 1)
				}
			},
			width()
			{
				if (this.IsComplete)
				{
					this.Progress.width = this.sliderWidth + "px"
				}
			}
		},
		methods:
		{
			fadeTextNow()
			{
				let ReversePercent = ((this.sliderWidth - this.height) * (100 / this.HandlerPosition)) / 1000 - 0.1;
				if (ReversePercent < 0)
					ReversePercent = 0;
				else if (ReversePercent > 1)
					ReversePercent = 1;
				if (this.CanMove) { this.Text.opacity = ReversePercent }
			},
			slideStart(e)
			{
				const THIS = this
				if (!this.IsComplete)
				{
					this.CanMove = true
					this.StartPositionWindow = (e.pageX || e.touches[0].pageX) - parseInt(this.$refs.handler.style.left.replace("px", ""), 10)
				}
				document.addEventListener("mousemove", this.slideMoving);
				document.addEventListener("mouseup", this.documentMouseUp);
			},
			documentMouseUp(e)
			{
				this.slideFinish(e);
				document.removeEventListener("mousemove", this.slideMoving);
				document.removeEventListener("mouseup", this.documentMouseUp);
			},
			slideMoving(e)
			{
				if (this.CanMove && !this.IsComplete)
				{
					if (typeof e.pageX !== "undefined" && e.pageX !== null)
						this.HandlerPosition = e.pageX - this.StartPositionWindow;
					else if (e.touches && e.touches.length)
						this.HandlerPosition = e.touches[0].pageX - this.StartPositionWindow;
					else
						return;
					if (this.HandlerPosition < 0)
						this.HandlerPosition = 0;
					if (this.HandlerPosition >= 0 && this.HandlerPosition <= (this.sliderWidth - this.height))
					{
						this.Handler.left = this.HandlerPosition + "px";
						this.Progress.width = (this.HandlerPosition + this.height / 2) + "px";
						this.fadeText();
					}
					else if (this.HandlerPosition > (this.sliderWidth - this.height))
					{
						this.Handler.left = (this.sliderWidth - this.height) + "px";
						this.Progress.width = (this.sliderWidth - (this.height / 2)) + "px";
						if (!this.requireReleaseToComplete)
							this.passVerify();
					}
				}
			},
			slideFinish(e)
			{
				if (this.CanMove && this.HandlerPosition > (this.sliderWidth - this.height))
				{
					this.Handler.left = (this.sliderWidth - this.height) + "px"
					this.Progress.width = this.sliderWidth + "px";
					this.passVerify()
				}
				if (this.CanMove && !this.IsComplete)
				{
					this.HandlerPosition = (e.pageX || e.changedTouches[0].pageX) - this.StartPositionWindow
					if (this.HandlerPosition < (this.sliderWidth - this.height))
					{
						this.Handler.left = 0 + "px"
						this.Progress.width = 0 + (this.height / 2) + "px"
						this.Text.opacity = 1
					}
					this.CanMove = false
				}
			},
			/** Arrow Right/Up moves the handle a quarter of the way; reaching the end completes.  Arrow Left/Down, Home and Escape move it back to the start. */
			keyDown(e)
			{
				if (this.disabled || this.IsComplete || this.CanMove)
					return;
				let travel = this.sliderWidth - this.height;
				if (e.key === "ArrowRight" || e.key === "ArrowUp")
				{
					e.preventDefault();
					this.HandlerPosition = Math.min(travel, Math.max(0, this.HandlerPosition) + travel / 4);
					if (this.HandlerPosition >= travel - 1)
					{
						this.Handler.left = travel + "px";
						this.passVerify();
						return;
					}
					this.Handler.left = this.HandlerPosition + "px";
					this.Progress.width = (this.HandlerPosition + this.height / 2) + "px";
					this.Text.opacity = 1 - this.HandlerPosition / travel;
				}
				else if (e.key === "ArrowLeft" || e.key === "ArrowDown" || e.key === "Home" || e.key === "Escape")
				{
					if (e.key !== "Escape")
						e.preventDefault();
					this.HandlerPosition = 0;
					this.Handler.left = 0 + "px";
					this.Progress.width = 0 + (this.height / 2) + "px";
					this.Text.opacity = 1;
				}
			},
			passVerify()
			{
				this.IsComplete = true
				this.CanMove = false
				this.Progress.width = this.sliderWidth + "px";
				this.$emit("completed")
			},
			reset()
			{
				this.CanMove = false
				this.IsComplete = false
				this.StartPositionWindow = 0
				this.HandlerPosition = 0
				this.Handler.left = 0
				this.Progress.width = 0
				this.Text.opacity = 1
			}
		}
	}
</script>

<style>
	.slideunlock
	{
		--su-size-text: 24px;
		--su-size-padding: 6px;
		--su-color-bg: #ebebeb;
		--su-color-progress-normal-bg: #cacaca;
		--su-color-progress-complete-bg: #42b983;
		--su-color-text-normal: #4f4f4f;
		--su-color-text-complete: #fefefe;
		--su-color-handler-bg: #fff;
		--su-icon-handler: url(data:image/svg;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAA3hpVFh0WE1MOmNvbS5hZG9iZS54bXAAAAAAADw/eHBhY2tldCBiZWdpbj0i77u/IiBpZD0iVzVNME1wQ2VoaUh6cmVTek5UY3prYzlkIj8+IDx4OnhtcG1ldGEgeG1sbnM6eD0iYWRvYmU6bnM6bWV0YS8iIHg6eG1wdGs9IkFkb2JlIFhNUCBDb3JlIDUuNS1jMDIxIDc5LjE1NTc3MiwgMjAxNC8wMS8xMy0xOTo0NDowMCAgICAgICAgIj4gPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4gPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiIgeG1sbnM6eG1wTU09Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC9tbS8iIHhtbG5zOnN0UmVmPSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvc1R5cGUvUmVzb3VyY2VSZWYjIiB4bWxuczp4bXA9Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC8iIHhtcE1NOk9yaWdpbmFsRG9jdW1lbnRJRD0ieG1wLmRpZDo0ZDhlNWY5My05NmI0LTRlNWQtOGFjYi03ZTY4OGYyMTU2ZTYiIHhtcE1NOkRvY3VtZW50SUQ9InhtcC5kaWQ6NTEyNTVEMURGMkVFMTFFNEI5NDBCMjQ2M0ExMDQ1OUYiIHhtcE1NOkluc3RhbmNlSUQ9InhtcC5paWQ6NTEyNTVEMUNGMkVFMTFFNEI5NDBCMjQ2M0ExMDQ1OUYiIHhtcDpDcmVhdG9yVG9vbD0iQWRvYmUgUGhvdG9zaG9wIENDIDIwMTQgKE1hY2ludG9zaCkiPiA8eG1wTU06RGVyaXZlZEZyb20gc3RSZWY6aW5zdGFuY2VJRD0ieG1wLmlpZDo2MTc5NzNmZS02OTQxLTQyOTYtYTIwNi02NDI2YTNkOWU5YmUiIHN0UmVmOmRvY3VtZW50SUQ9InhtcC5kaWQ6NGQ4ZTVmOTMtOTZiNC00ZTVkLThhY2ItN2U2ODhmMjE1NmU2Ii8+IDwvcmRmOkRlc2NyaXB0aW9uPiA8L3JkZjpSREY+IDwveDp4bXBtZXRhPiA8P3hwYWNrZXQgZW5kPSJyIj8+YiRG4AAAALFJREFUeNpi/P//PwMlgImBQkA9A+bOnfsIiBOxKcInh+yCaCDuByoswaIOpxwjciACFegBqZ1AvBSIS5OTk/8TkmNEjwWgQiUgtQuIjwAxUF3yX3xyGIEIFLwHpKyAWB+I1xGSwxULIGf9A7mQkBwTlhBXAFLHgPgqEAcTkmNCU6AL9d8WII4HOvk3ITkWJAXWUMlOoGQHmsE45ViQ2KuBuASoYC4Wf+OUYxz6mQkgwAAN9mIrUReCXgAAAABJRU5ErkJggg==);
		user-select: none;
	}

	@-webkit-keyframes animated
	{
		0%
		{
			background-position: -300px 0
		}

		to
		{
			background-position: 300px 0
		}
	}

	@keyframes animated
	{
		0%
		{
			background-position: -300px 0
		}

		to
		{
			background-position: 300px 0
		}
	}

	@-webkit-keyframes shine
	{
		to
		{
			width: 100%;
			transition: all 5s ease-out;
			background-color: hsla(0,0%,100%,0)
		}
	}

	@keyframes shine
	{
		to
		{
			width: 100%;
			transition: all 5s ease-out;
			background-color: hsla(0,0%,100%,0)
		}
	}

	.slideunlock
	{
		display: flex;
		position: relative;
		box-sizing: content-box;
		padding: var(--su-size-padding);
		background-color: var(--su-color-bg);
		text-align: center
	}

		.slideunlock:after
		{
			content: "";
			position: absolute;
			top: 0;
			left: 0;
			width: 0;
			height: 100%;
			transition: none;
			-webkit-animation: shine 4s infinite;
			animation: shine 4s infinite;
			background-color: hsla(0,0%,100%,.4)
		}

		.slideunlock .slideunlock-progressbar
		{
			position: absolute;
			z-index: 1;
			width: 0;
			height: 34px;
			transition: background 1s ease-out;
			background-color: var(--su-color-progress-normal-bg)
		}

		.slideunlock .slideunlock-text
		{
			display: inline-flex;
			padding-left: 58px;
			z-index: 2;
			align-items: center;
			justify-content: center;
			width: 100%;
			transition: background 1s ease,color 1s ease;
			-webkit-animation: animated 2s linear infinite;
			animation: animated 2s linear infinite;
			background: var(--su-color-text-normal) linear-gradient(90deg,hsla(0,0%,100%,0),#fff,hsla(0,0%,100%,0)) no-repeat;
			-webkit-background-clip: text;
			background-clip: text;
			color: rgba(0,0,0,0.5);
			font-size: var(--su-size-text);
			-webkit-user-select: none;
			-moz-user-select: none;
			-ms-user-select: none;
			user-select: none
		}

			.slideunlock .slideunlock-text:before
			{
				content: "";
				display: inline-block;
				height: 100%;
				vertical-align: middle
			}

		.slideunlock .slideunlock-handler
		{
			position: absolute;
			z-index: 3;
			top: 0;
			left: 0;
			margin: var(--su-size-padding);
			transition: background .75s ease,transform .1s ease-in;
			background: var(--su-color-handler-bg) var(--su-icon-handler) no-repeat center;
			cursor: -webkit-grab;
			cursor: grab
		}

			.slideunlock .slideunlock-handler:active
			{
				transform: scale(1.05);
				cursor: -webkit-grabbing;
				cursor: grabbing
			}

			.slideunlock .slideunlock-handler:focus-visible
			{
				outline: 2px solid #1A5FB4;
				outline-offset: 2px
			}

		/* "color" property: only the handle and the border take the color, the track stays neutral. */
		.slideunlock.is-themed
		{
			border: 2px solid var(--su-color-theme)
		}

			.slideunlock.is-themed .slideunlock-handler
			{
				background: var(--su-color-theme) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'%3E%3Cpath d='M3 3l5 5-5 5M8 3l5 5-5 5' fill='none' stroke='%23FFFFFF' stroke-width='2'/%3E%3C/svg%3E") no-repeat center
			}

		.slideunlock.is-circle
		{
			border-radius: 50rem
		}

			.slideunlock.is-circle:after
			{
				border-radius: 50rem 0 0 50rem
			}

			.slideunlock.is-circle .slideunlock-handler
			{
				border-radius: 50%
			}

		.slideunlock.is-noanimate:after
		{
			-webkit-animation: none;
			animation: none
		}

		.slideunlock.is-noanimate .slideunlock-text
		{
			-webkit-animation: none;
			animation: none;
			color: var(--su-color-text-normal)
		}

		.slideunlock.is-disabled
		{
			opacity: .5
		}

			.slideunlock.is-disabled:after
			{
				content: "";
				position: absolute;
				z-index: 10;
				top: 0;
				left: 0;
				width: 100%;
				height: 100%;
				-webkit-animation: none;
				animation: none;
				opacity: .5;
				background-color: var(--su-color-bg)
			}

			.slideunlock.is-disabled .slideunlock-text
			{
				-webkit-animation: none;
				animation: none;
				opacity: .25;
				color: var(--su-color-text-normal)
			}

			.slideunlock.is-disabled .slideunlock-handler
			{
				pointer-events: none
			}

			.slideunlock.is-disabled.is-circle:after
			{
				border-radius: 50rem
			}

		.slideunlock.is-complete:after
		{
			-webkit-animation: none;
			animation: none
		}

		.slideunlock.is-complete .slideunlock-progressbar
		{
			background-color: var(--su-color-progress-complete-bg)
		}

		.slideunlock.is-complete .slideunlock-text
		{
			-webkit-animation: none;
			animation: none;
			opacity: 1 !important;
			color: var(--su-color-text-complete)
		}

		.slideunlock.is-complete .slideunlock-handler
		{
			opacity: 0;
			background: var(--su-color-progress-complete-bg)
		}
</style>